import { useEffect, useState } from "react";
import { BrowserRouter, Link, Navigate, Route, Routes } from "react-router-dom";
import { LogOut, Menu, MessageCircle, UserRound } from "lucide-react";
import { Toaster } from "sonner";

import "@/App.css";
import { api, setAuthToken } from "@/api";
import AuthPage from "@/pages/AuthPage";
import JobsPage from "@/pages/JobsPage";
import ManagerDashboard from "@/pages/ManagerDashboard";
import MessagesPage from "@/pages/MessagesPage";
import PortfolioPage from "@/pages/PortfolioPage";
import PublicProfilePage from "@/pages/PublicProfilePage";
import NotFoundPage from "@/pages/NotFoundPage";

function Header({ user, onLogout }) {
  return <header className="site-header" data-testid="site-header">
    <Link className="brand" data-testid="brand-link" to="/jobs">CREWED<span>®</span></Link>
    <nav data-testid="site-navigation">
      <Link data-testid="jobs-nav-link" to="/jobs">Opportunities</Link>
      <Link data-testid="talent-nav-link" to="/talent">Talent</Link>
      {user?.role === "manager" && <Link data-testid="manager-nav-link" to="/manager">Workspace</Link>}
      {user && <Link data-testid="messages-nav-link" to="/messages"><MessageCircle size={16} />Messages</Link>}
    </nav>
    <div className="header-actions">
      {user ? <><span className="user-name" data-testid="header-user-name"><UserRound size={15} />{user.name}</span><button data-testid="logout-button" onClick={onLogout} title="Log out"><LogOut size={17} /></button></> : <><Link data-testid="sign-in-link" to="/join/freelancer">Sign in</Link><Link className="header-cta" data-testid="hire-crew-link" to="/join/manager">Hire a crew</Link></>}
      <button className="menu-button" data-testid="mobile-menu-button" title="Menu"><Menu size={20} /></button>
    </div>
  </header>;
}

function SessionLoading() {
  return <main className="session-loading" data-testid="session-loading">Loading your workspace…</main>;
}

function App() {
  const [user, setUser] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);

  useEffect(() => {
    const saved = localStorage.getItem("crewed-session");
    if (!saved) {
      setSessionLoading(false);
      return;
    }
    try {
      const session = JSON.parse(saved);
      setAuthToken(session.token);
      api.get("/auth/me")
        .then(({ data }) => setUser({ ...data, token: session.token }))
        .catch(() => localStorage.removeItem("crewed-session"))
        .finally(() => setSessionLoading(false));
    } catch (error) {
      localStorage.removeItem("crewed-session");
      setSessionLoading(false);
    }
  }, []);

  const logout = () => {
    localStorage.removeItem("crewed-session");
    setAuthToken("");
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser((current) => {
      const nextUser = { ...current, ...updates };
      localStorage.setItem("crewed-session", JSON.stringify(nextUser));
      return nextUser;
    });
  };

  return <BrowserRouter><Header onLogout={logout} user={user} /><Routes>
    <Route element={<Navigate to="/jobs" replace />} path="/" />
    <Route element={<JobsPage user={user} />} path="/jobs" />
    <Route element={<PortfolioPage onUserChange={updateUser} user={user} />} path="/talent" />
    <Route element={<PublicProfilePage user={user} />} path="/talent/:slug" />
    <Route element={<AuthPage onAuth={setUser} />} path="/join/:role" />
    <Route element={sessionLoading ? <SessionLoading /> : user?.role === "manager" ? <ManagerDashboard /> : <Navigate to="/join/manager" replace />} path="/manager" />
    <Route element={sessionLoading ? <SessionLoading /> : user ? <MessagesPage user={user} /> : <Navigate to="/join/freelancer" replace />} path="/messages" />
    <Route element={<NotFoundPage />} path="*" />
  </Routes><Toaster position="bottom-right" richColors /></BrowserRouter>;
}

export default App;
